import React, { useState, useCallback, useEffect, useRef, createContext, useContext } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import webChatHistoryService from '../services/webChatHistoryService';
import { useAuth } from '../contexts/AuthContext';
import { EinstellungenKontext } from '../pages/einstellungen/EinstLink';
import { researchLock } from '../pages/accessSummary';
import { useRISearch } from '../contexts/RISearchContext';
import ErrorBoundary from './ErrorBoundary';
// Profil-Nachbau F-06 (TEIL-3 SP3-05, PF-311-1): Hinweisleiste bei einer laufenden Störung.
import StoerungsLeiste from './hilfe/StoerungsLeiste';
import RISViewerModal from './RISViewerModal';

// Unterhalb dieser Breite liegt die Leiste als Overlay über dem Inhalt (index.css, Block max-width: 767px).
const MOBILE_SHELL_QUERY = '(max-width: 767px)';
const isMobileShell = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_SHELL_QUERY).matches;

// D6: der geöffnete, am Server gespeicherte Chat dieses Tabs; ein Reload von /ris öffnet ihn wieder (samt laufendem Lauf).
const OPEN_CHAT_KEY = 'risearch-open-chat';
function readOpenChat(userId: unknown): string | null {
  try {
    const stored = JSON.parse(sessionStorage.getItem(OPEN_CHAT_KEY) || 'null');
    return stored && stored.userId === userId && typeof stored.sessionId === 'string' ? stored.sessionId : null;
  } catch {
    return null;
  }
}
function rememberOpenChat(userId: unknown, sessionId: string | null): void {
  try {
    if (sessionId) sessionStorage.setItem(OPEN_CHAT_KEY, JSON.stringify({ userId, sessionId }));
    else sessionStorage.removeItem(OPEN_CHAT_KEY);
  } catch {
    // Ohne Speicher des Tabs beginnt ein Reload wie bisher mit einem neuen Chat.
  }
}

interface AppLayoutContextType {
  currentSessionId: string | null;
  setCurrentSessionId: (id: string | null) => void;
  triggerSidebarRefresh: () => void;
  sidebarRefreshTrigger: number;
  onNewChat: (initialQuestion?: string) => Promise<string>;
  onSessionSelect: (sessionId: string) => void;
  pendingSessionLoad: { sessionId: string } | null;
  clearPendingSessionLoad: () => void;
}

const AppLayoutContext = createContext<AppLayoutContextType | undefined>(undefined);

export function useAppLayout(): AppLayoutContextType {
  const context = useContext(AppLayoutContext);
  if (!context) {
    throw new Error('useAppLayout must be used within an AppLayout');
  }
  return context;
}

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { setOpenSession } = useRISearch();

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(isMobileShell);
  // Nachtrag A2: Der Knopf „Seitenleiste ausklappen“ in der eingeklappten Leiste. Nach Escape bekommt er
  // den Fokus zurück; ohne das landet der Tastaturnutzer am Dokumentanfang (AppSidebar schaltet die
  // beiden Zustände über visibility, und ein verstecktes Element verliert den Fokus an document.body).
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  // Nur der Escape-Weg gibt den Fokus zurück. Beim Einklappen per Klick oder nach einer Navigation
  // liegt der Fokus woanders und darf nicht in die Leiste gezogen werden.
  const returnFocusOnCollapse = useRef(false);

  // Beim Wechsel auf Mobilbreite nur einklappen. Nie automatisch ausklappen,
  // sonst überschreibt der Listener die manuelle Wahl am Desktop.
  useEffect(() => {
    const query = window.matchMedia(MOBILE_SHELL_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setSidebarCollapsed(true);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const collapseOnMobile = useCallback(() => {
    if (isMobileShell()) setSidebarCollapsed(true);
  }, []);

  // Menülinks und jede andere Navigation innerhalb der Hülle klappen die Leiste mobil ein. Seit AE-1 Nachtrag 3 rendert
  // die Hülle hinter dem Einstellungen-Pop-up mit ihrer eigenen Adresse; das Öffnen zählt über die echte Adresse mit.
  const einstellungen = useContext(EinstellungenKontext);
  useEffect(() => {
    collapseOnMobile();
  }, [location.key, einstellungen.ort?.key, collapseOnMobile]);

  // Nachtrag A1: Escape klappt die mobil als Overlay liegende Leiste ein. Der Backdrop trägt
  // aria-hidden und ist per Tastatur nicht erreichbar; ohne Escape bliebe die Leiste offen.
  // Der Listener hängt nur bei offener Leiste und nur mobil und wird beim Aufräumen entfernt.
  useEffect(() => {
    if (sidebarCollapsed || !isMobileShell()) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Ein offener Dialog behandelt Escape selbst: die Portal-Dialoge am document.body
      // (.editorial-modal-backdrop) und das native <dialog>, etwa das Fenster der Startseite (Dashboard.tsx),
      // das keinen Backdrop-Knoten hat, sondern nur ::backdrop (Nachtrag A2).
      if (document.querySelector('.editorial-modal-backdrop, dialog[open]')) return;
      // Eingabefelder (z. B. Umbenennen in der Leiste) behalten Escape für ihren Abbruch.
      const target = event.target;
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      // Nach einem Wechsel auf Desktopbreite liegt die Leiste nicht mehr als Overlay.
      if (!isMobileShell()) return;
      returnFocusOnCollapse.current = true;
      setSidebarCollapsed(true);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [sidebarCollapsed]);

  // Nachtrag A2: Fokus zurück auf „Seitenleiste ausklappen“, sobald die Leiste eingeklappt gerendert ist.
  // Der Effekt läuft nach dem Schreiben des DOM; der Knopf ist dann sichtbar und anfokussierbar.
  useEffect(() => {
    if (!sidebarCollapsed || !returnFocusOnCollapse.current) return;
    returnFocusOnCollapse.current = false;
    expandButtonRef.current?.focus();
  }, [sidebarCollapsed]);
  const [restoredChat] = useState(() => (location.pathname === '/ris' ? readOpenChat(user?.id) : null));
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(restoredChat);
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
  const [pendingSessionLoad, setPendingSessionLoad] = useState<{ sessionId: string } | null>(
    restoredChat ? { sessionId: restoredChat } : null,
  );
  const currentSessionIdRef = useRef(currentSessionId);
  currentSessionIdRef.current = currentSessionId;

  const triggerSidebarRefresh = useCallback(() => {
    setSidebarRefreshTrigger(prev => prev + 1);
  }, []);

  const clearPendingSessionLoad = useCallback(() => {
    setPendingSessionLoad(null);
  }, []);

  const handleNewChat = useCallback(async (initialQuestion?: string): Promise<string> => {
    // Paket 12, RSM-25: ohne Recherchezugang startet keine neue Suche; die Leiste sperrt den Knopf, das hier hält
    // jeden anderen Weg an, ohne Navigation und ohne Serveraufruf.
    if (researchLock(user?.access).locked) return currentSessionId || '';
    collapseOnMobile();
    // UI-14: „Neue Recherche“ öffnet für jedes Konto eine neue Recherche; den Vergleich öffnet ein Entwicklerkonto über
    // „A/B-Vergleich“.
    // Generate new session ID
    const newSessionId = await webChatHistoryService.generateSessionId('ris', user?.id);
    rememberOpenChat(user?.id, null);
    setCurrentSessionId(newSessionId);
    navigate('/ris', {
      state: initialQuestion?.trim() ? {
        researchPrompt: initialQuestion.trim(),
        submitResearch: true,
        researchSessionId: newSessionId,
      } : null,
    });
    return newSessionId;
  }, [navigate, user?.id, user?.access, currentSessionId, collapseOnMobile]);

  const handleSessionSelect = useCallback((sessionId: string) => {
    // Allow session switching at any time (user may want to view other sessions)
    collapseOnMobile();
    rememberOpenChat(user?.id, sessionId);
    setCurrentSessionId(sessionId);
    setPendingSessionLoad({ sessionId });
    navigate('/ris');
  }, [navigate, collapseOnMobile, user?.id]);

  // UI-17: der Chat, der gerade vor dem Nutzer steht; für ihn meldet der Recherchekontext kein Ende als Toast. Das
  // Einstellungen-Pop-up verdeckt ihn.
  const chatInView = location.pathname === '/ris' && !einstellungen.offen ? currentSessionId : null;
  useEffect(() => {
    setOpenSession(chatInView);
    return () => setOpenSession(null);
  }, [chatInView, setOpenSession]);

  // Ein neuer Chat ist ab seiner ersten Frage am Server gespeichert; ab dann öffnet ihn ein Reload wieder (D6).
  // „Anzeigen“ am Toast eines beendeten Laufs öffnet dessen Chat (UI-17).
  // D4: wird der geöffnete Chat gelöscht, steht der leere Start.
  useEffect(() => {
    const created = (event: Event) => {
      const sessionId = (event as CustomEvent<{ sessionId?: string }>).detail?.sessionId;
      if (sessionId && sessionId === currentSessionIdRef.current) rememberOpenChat(user?.id, sessionId);
    };
    const open = (event: Event) => {
      const sessionId = (event as CustomEvent<{ sessionId?: string }>).detail?.sessionId;
      if (sessionId) handleSessionSelect(sessionId);
    };
    const deleted = (event: Event) => {
      const sessionId = (event as CustomEvent<{ sessionId?: string }>).detail?.sessionId;
      if (!sessionId || sessionId !== currentSessionIdRef.current) return;
      rememberOpenChat(user?.id, null);
      setPendingSessionLoad(null);
      void webChatHistoryService.generateSessionId('ris', user?.id).then(newSessionId => {
        if (currentSessionIdRef.current === sessionId) setCurrentSessionId(newSessionId);
      });
    };
    window.addEventListener('session:created', created);
    window.addEventListener('session:open', open);
    window.addEventListener('session:deleted', deleted);
    return () => {
      window.removeEventListener('session:created', created);
      window.removeEventListener('session:open', open);
      window.removeEventListener('session:deleted', deleted);
    };
  }, [user?.id, handleSessionSelect]);

  const contextValue: AppLayoutContextType = {
    currentSessionId,
    setCurrentSessionId,
    triggerSidebarRefresh,
    sidebarRefreshTrigger,
    onNewChat: handleNewChat,
    onSessionSelect: handleSessionSelect,
    pendingSessionLoad,
    clearPendingSessionLoad,
  };

  return (
    <AppLayoutContext.Provider value={contextValue}>
      <div className="editorial-app-shell min-h-screen h-screen flex bg-warm-gradient">
        {/* Wie in der Verwaltung: an der Chatliste vorbei direkt zum Inhalt. */}
        <a
          href="#hauptinhalt"
          className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[200] focus:rounded-[3px] focus:bg-warm-card focus:px-4 focus:py-3 focus:text-sm focus:font-semibold focus:text-primary focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent"
        >
          Zum Inhalt
        </a>
        <AppSidebar
          currentSessionId={currentSessionId}
          onSessionSelect={handleSessionSelect}
          onNewChat={handleNewChat}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          expandButtonRef={expandButtonRef}
          refreshTrigger={sidebarRefreshTrigger}
        />
        {!sidebarCollapsed && (
          <div
            className="editorial-sidebar-backdrop"
            aria-hidden="true"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
        <main id="hauptinhalt" tabIndex={-1} className="flex-1 flex flex-col min-w-0 overflow-hidden focus:outline-none">
          <StoerungsLeiste />
          {/* Ein Fehler beim Darstellen einer Ansicht lässt die Leiste stehen; ein Wechsel der Ansicht versucht es neu. */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
        {/* F-27: die Dokumentansicht steht in der Hülle und nimmt deren zoom; eine für alle Seiten der App, auch den Vergleich. */}
        <RISViewerModal />
      </div>
    </AppLayoutContext.Provider>
  );
};

export default AppLayout;
